import appSettings from '../config/settings';

const SCRIPT_ID = 'recaptcha-v3-script';

// Google's script comes from a third party over a connection we don't control, and
// an ad-blocker or a corporate proxy can simply drop the request. Without a ceiling,
// a request that never settles leaves the submit button spinning forever, which the
// person in front of it can't tell apart from the site being broken.
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

// A check that can't be completed must not be confused with a check that says "bot".
// The first is our problem (missing site key, blocked script, server down) and the
// person signing up should not be punished for it; the second is the one worth acting
// on. Everything that means "we couldn't ask Google" carries this code.
export const RECAPTCHA_UNAVAILABLE = 'RECAPTCHA_UNAVAILABLE';

/** Error thrown when the reCAPTCHA check couldn't be run at all. */
export class RecaptchaUnavailableError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'RecaptchaUnavailableError';
    this.code = RECAPTCHA_UNAVAILABLE;
    this.cause = cause;
  }
}

/**
 * Was this error caused by reCAPTCHA being unreachable, rather than by a verdict?
 *
 * @param {*} error any thrown value
 * @returns {boolean}
 */
export const isRecaptchaUnavailableError = error => error?.code === RECAPTCHA_UNAVAILABLE;

let scriptLoadingPromise = null;

// Loads the reCAPTCHA v3 script once and resolves once `window.grecaptcha` is ready.
//
// A failed load is *not* cached: the usual cause is a transient network blip, and
// remembering the rejection would mean one bad moment disables reCAPTCHA (and, before
// the retry handling below existed, sign-up itself) for the rest of the page's life.
const loadRecaptchaScript = siteKey => {
  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  scriptLoadingPromise = new Promise((resolve, reject) => {
    // The script may already have finished loading - e.g. a second form on the same
    // page - in which case no further load event is coming and waiting for one hangs.
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }

    const timeoutId = setTimeout(
      () => reject(new RecaptchaUnavailableError('reCAPTCHA script load timed out')),
      SCRIPT_LOAD_TIMEOUT_MS
    );
    const settle = (fn, value) => {
      clearTimeout(timeoutId);
      fn(value);
    };
    const onError = e =>
      settle(reject, new RecaptchaUnavailableError('reCAPTCHA script failed to load', e));

    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', () => settle(resolve, window.grecaptcha));
      existingScript.addEventListener('error', onError);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => settle(resolve, window.grecaptcha);
    script.onerror = onError;
    document.head.appendChild(script);
  }).catch(e => {
    scriptLoadingPromise = null;
    throw e;
  });

  return scriptLoadingPromise;
};

/**
 * Generates a reCAPTCHA v3 token for the given action.
 *
 * Resolves to `null` if no site key is configured (e.g. local dev without
 * REACT_APP_RECAPTCHA_SITE_KEY set), so callers can treat verification as skipped.
 *
 * Rejects with a {@link RecaptchaUnavailableError} when the check couldn't be run.
 *
 * @param {string} action - The reCAPTCHA action name (e.g. "auth")
 * @returns {Promise<string|null>}
 */
export const getRecaptchaToken = action => {
  const siteKey = appSettings.recaptchaSiteKey;
  if (!siteKey) {
    console.error('REACT_APP_RECAPTCHA_SITE_KEY is not configured - skipping reCAPTCHA check.');
    return Promise.resolve(null);
  }

  return loadRecaptchaScript(siteKey).then(
    grecaptcha =>
      new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(siteKey, { action })
            .then(resolve, e =>
              reject(new RecaptchaUnavailableError('reCAPTCHA execution failed', e))
            );
        });
      })
  );
};

// Exposed for tests: the module-level script promise otherwise leaks between cases.
export const resetRecaptchaScriptForTesting = () => {
  scriptLoadingPromise = null;
};
